import React, { useState, useEffect } from 'react';
import { Paper, Box, TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent } from '@mui/material';
import { ApiService, ApiEndpoints } from '../../../services/ApiService';

/**
 * Framework interface
 */
interface Framework {
  id: string | number;
  name: string;
}

/**
 * Obligation interface
 */
interface Obligation {
  id: string | number;
  name: string;
}

/**
 * Filter interface
 */
export interface ControlFiltersState {
  search: string;
  status: 'all' | 'active' | 'draft' | 'archived';
  obligationId: string | null;
  frameworkId: string | null;
}

/**
 * Props interface
 */
interface ControlFiltersProps {
  filters: ControlFiltersState;
  onFilterChange: (filters: Partial<ControlFiltersState>) => void;
  hideObligationFilter?: boolean;
  hideFrameworkFilter?: boolean;
}

/**
 * Control Filters Component
 * 
 * Provides filtering options for the ControlList component
 */
export default function ControlFilters({ 
  filters, 
  onFilterChange,
  hideObligationFilter = false,
  hideFrameworkFilter = false,
}: ControlFiltersProps) {
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [obligations, setObligations] = useState<Obligation[]>([]);

  /**
   * Fetch frameworks and obligations for filter dropdowns
   */
  useEffect(() => {
    const fetchFiltersData = async () => {
      try {
        // Only fetch frameworks if not hidden
        if (!hideFrameworkFilter) {
          const frameworksResponse = await ApiService.get<Framework[]>(ApiEndpoints.FRAMEWORKS.LIST);
          setFrameworks(frameworksResponse);
        }
        
        // Only fetch obligations if not hidden
        if (!hideObligationFilter) {
          const obligationsResponse = await ApiService.get<Obligation[]>(ApiEndpoints.OBLIGATIONS.LIST);
          setObligations(obligationsResponse);
        }
      } catch (err) {
        console.error('Error fetching filter data:', err);
        // Fallback to mock data
        if (!hideFrameworkFilter) {
          setFrameworks([
            { id: 1, name: 'ISO 27001' },
            { id: 2, name: 'SOC 2' },
            { id: 3, name: 'GDPR' },
          ]);
        }
        
        if (!hideObligationFilter) {
          setObligations([
            { id: 1, name: 'Information Security Policy' },
            { id: 2, name: 'Access Control' },
            { id: 3, name: 'Data Protection Impact Assessments' },
          ]);
        }
      }
    };

    fetchFiltersData();
  }, [hideFrameworkFilter, hideObligationFilter]);

  /**
   * Handle search input change
   */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ search: event.target.value });
  };

  /**
   * Handle status select change
   */
  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    onFilterChange({ status: event.target.value as any });
  };

  /**
   * Handle framework select change
   */
  const handleFrameworkChange = (event: SelectChangeEvent<string>) => {
    onFilterChange({ frameworkId: event.target.value === 'all' ? null : event.target.value });
  };

  /**
   * Handle obligation select change
   */
  const handleObligationChange = (event: SelectChangeEvent<string>) => {
    onFilterChange({ obligationId: event.target.value === 'all' ? null : event.target.value });
  };

  /**
   * Render component
   */
  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box 
        display="flex" 
        flexDirection={{ xs: 'column', lg: 'row' }} 
        gap={2}
        flexWrap="wrap"
      >
        <TextField
          label="Search Controls"
          variant="outlined"
          size="small"
          fullWidth={!(!hideFrameworkFilter && !hideObligationFilter)}
          sx={{ minWidth: { xs: '100%', md: 200 }, flex: 1 }}
          value={filters.search}
          onChange={handleSearchChange}
          placeholder="Search by name or description"
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={filters.status}
            label="Status"
            onChange={handleStatusChange}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>
        
        {!hideFrameworkFilter && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="framework-filter-label">Framework</InputLabel>
            <Select
              labelId="framework-filter-label"
              value={filters.frameworkId || 'all'}
              label="Framework"
              onChange={handleFrameworkChange}
            >
              <MenuItem value="all">All Frameworks</MenuItem>
              {frameworks.map(framework => (
                <MenuItem key={framework.id} value={String(framework.id)}>
                  {framework.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        {!hideObligationFilter && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="obligation-filter-label">Obligation</InputLabel>
            <Select
              labelId="obligation-filter-label"
              value={filters.obligationId || 'all'}
              label="Obligation"
              onChange={handleObligationChange}
            >
              <MenuItem value="all">All Obligations</MenuItem>
              {obligations.map(obligation => (
                <MenuItem key={obligation.id} value={String(obligation.id)}>
                  {obligation.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    </Paper>
  );
}
